import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Select, { components } from "react-select";
import InputWithLabel from "../../../Components/InputWithLabel";
import TextArea from "../../../Components/TextArea";
import { GetClientUserListing } from "../../../Api/clientUser";
import { useMutateSendCustomNotification } from "../hooks/useApiHook";
import { ErrorToast, SuccessToast } from "../../../Utils/SweetAlert";
import SaveButton from '../../../Components/SaveButton';

const CustomOption = (props) => {
  const { children, ...rest } = props;
  return (
    <components.Option {...rest}>
      <div
        className="d-flex align-items-center"
        style={{
          cursor: "pointer",
          padding: "8px 15px",
          gap: "15px"
        }}
      >
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null}
          className="form-check-input"
          style={{
            cursor: "pointer",
            width: "18px",
            height: "18px",
            marginLeft: "2px",
            border: "1px solid #ced4da",
            borderRadius: "4px",
            flexShrink: 0
          }}
        />
        <span style={{ fontSize: "14px", marginLeft: "40px", color: "#333", fontWeight: "500" }}>
          {props.data.name || "Unknown"} - {props.data.email || "No Email"}
        </span>
      </div>
    </components.Option >
  );
};

const AddNotificationModal = ({ show, onHide, onSuccess }) => {
  const [users, setUsers] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const sendNotificationApi = useMutateSendCustomNotification();

  useEffect(() => {
    if (show) {
      fetchUsers();
    }
  }, [show]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await GetClientUserListing({ page: 1, limit: 1000 });
      const userOptions = response?.data?.data.map((user) => ({
        value: user._id,
        // label: `${user.name || "Unknown"} - ${user.email || ""}`,
        label: `${user.name || "Unknown"}`,
        name: user.name,
        email: user.email,
      })) || [];
      setUsers(userOptions);
    } catch (error) {
      ErrorToast("Failed to fetch users.");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const validationSchema = Yup.object().shape({
    title: Yup.string().required("Title is required").max(35, "Title should not exceed 35 characters"),
    message: Yup.string().required("Message is required").max(200, "Message should not exceed 200 characters"),
    isSendAll: Yup.boolean(),
    userId: Yup.array().when("isSendAll", {
      is: false,
      then: (schema) => schema.min(1, "Please select at least one recipient").required("Recipients are required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const handleSubmit = (values, { setSubmitting }) => {
    const payload = {
      title: values.title,
      message: values.message,
      isSendAll: values.isSendAll,
      userId: values.isSendAll ? [] : values.userId.map(u => u.value),
    };

    sendNotificationApi.mutate(payload, {
      onSuccess: (response) => {
        SuccessToast(response?.message || "Notification sent successfully.");
        onSuccess();
        onHide();
      },
      onError: (error) => {
        ErrorToast(error?.response?.data?.message || "Failed to send notification.");
      },
      onSettled: () => {
        setSubmitting(false);
      },
    });
  };

  const selectStyles = {
    control: (styles, state) => ({
      ...styles,
      border: state.isFocused ? "2px solid #AD5ADC" : "2px solid #AD5ADC80",
      boxShadow: "none",
      borderRadius: "12px",
      minHeight: 45,
      paddingLeft: "8px",
      paddingRight: "8px",
      cursor: "pointer",
      fontFamily: "Rubik, sans-serif",
      "&:hover": {
        border: "2px solid #AD5ADC",
      }
    }),
    option: (styles, { isFocused, isSelected }) => ({
      ...styles,
      backgroundColor: isSelected ? "#E3F2FD" : isFocused ? "#F8F9FA" : "white",
      color: "#333",
      cursor: "pointer",
      padding: 0,
      fontFamily: "Rubik, sans-serif",
      "&:active": {
        backgroundColor: "#E3F2FD",
      }
    }),
    multiValue: (styles) => ({
      ...styles,
      backgroundColor: "#AD5ADC15",
      borderRadius: "8px",
      margin: "2px",
      fontFamily: "Rubik, sans-serif",
    }),
    multiValueLabel: (styles) => ({
      ...styles,
      color: "#AD5ADC",
      fontWeight: "500",
      fontSize: "0.85rem",
      padding: "2px 6px",
      fontFamily: "Rubik, sans-serif",
    }),
    multiValueRemove: (styles) => ({
      ...styles,
      color: "#AD5ADC",
      fontFamily: "Rubik, sans-serif",
      "&:hover": {
        backgroundColor: "#AD5ADC",
        color: "white",
        borderRadius: "0 8px 8px 0",
      },
    }),
    placeholder: (styles) => ({
      ...styles,
      color: "#6c757d90",
      fontFamily: "Rubik, sans-serif",
    }),
    input: (styles) => ({
      ...styles,
      fontFamily: "Rubik, sans-serif",
    }),
    dropdownIndicator: (styles) => ({
      ...styles,
      color: "#6c757d",
      "&:hover": {
        color: "#333",
      }
    }),
    indicatorSeparator: (styles) => ({
      ...styles,
      backgroundColor: "#ced4da70",
      margin: "10px 0",
    }),
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      scrollable
      contentClassName="custom-notification-module"
    >
      <Modal.Header closeButton className="px-4">
        <Modal.Title className="fw-bold" style={{ color: "#000000", fontSize: "1.25rem" }}>
          Send Custom Notification
        </Modal.Title>
      </Modal.Header>
      <Formik
        initialValues={{ title: "", message: "", isSendAll: false, userId: [] }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, isSubmitting }) => (
          <Form>
            <Modal.Body className="px-4" style={{ maxHeight: "60vh", overflowY: "auto" }}>
              <div className="row">
                <div className="col-md-12 mb-3">
                  <Field
                    Label="Title"
                    name="title"
                    placeholder="Enter Title"
                    component={InputWithLabel}
                    maxLength={35}
                  />
                </div>
                <div className="col-md-12 mb-3">
                  <Field
                    Label="Message"
                    name="message"
                    placeholder="Enter Message"
                    component={TextArea}
                    height="110px"
                    maxLength={200}
                  />
                </div>
                <div className="col-md-12 mb-4">
                  <div className="form-check custom-checkbox">
                    <Field
                      type="checkbox"
                      name="isSendAll"
                      id="isSendAll"
                      className="form-check-input"
                      onChange={(e) => {
                        setFieldValue("isSendAll", e.target.checked);
                        if (e.target.checked) {
                          setFieldValue("userId", []);
                        }
                      }}
                      style={{ cursor: "pointer", width: "1.2rem", height: "1.2rem", borderColor: "#AD5ADC" }}
                    />
                    <label htmlFor="isSendAll" className="form-check-label ms-2 fw-medium" style={{ cursor: "pointer" }}>
                      Send to All Users
                    </label>
                  </div>
                </div>
                {!values.isSendAll && (
                  <div className="col-md-12 mb-3">
                    <label className="form-label fw-medium mb-2">
                      Select Recipients <span style={{ color: "#AD5ADC" }}>*</span>
                    </label>
                    <Select
                      isMulti
                      options={users}
                      isLoading={isLoadingUsers}
                      value={values.userId}
                      onChange={(selectedOptions) => setFieldValue("userId", selectedOptions)}
                      placeholder="Select..."
                      styles={selectStyles}
                      closeMenuOnSelect={false}
                      components={{ Option: CustomOption }}
                      hideSelectedOptions={false}
                    />
                    <ErrorMessage name="userId" component="div" className="text-danger small mt-1" />
                  </div>
                )}
              </div>
            </Modal.Body>
            <Modal.Footer className="border-0 px-4 pb-4">
              <SaveButton loading={isSubmitting} />
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default AddNotificationModal;
